import { addListener } from "~/lib/signals";
import Scene from "~/scene";
import Wasm from "../wasm";
import WasmStoreMapping from "../mapping";
import { watch, watchCoordinates, watchFit, watchMapsetAndDesaturate, watchOphotoTimeline } from "~/state/util";
import { store } from "~/state/store";
import { setCoordinates, setFit } from "~/state/map/state";
import { deferPromise } from "~/util/general";
import MapContextMenu from "~/ui/controls/mapcontextmenu";
import { LAYER_MAPPING } from "../const";
import { MAP_OVERLAYS } from "~/state/map/constants";
import { TWasmBaseMaps } from "~/interfaces";
import { IPoiData } from "../interfaces";
import { CONFIG } from "~/config";
import WasmHiddenPois from "../hidden-pois";
import WasmSharePosition from "../share-position";
import { onMapClick } from "./click";
import { onHover } from "./hover";
import { onDragEnd, onDragMove, onDragStart } from "./dnd";
import CursorManager from "~/util/cursorManager";
import { setItem } from "~/util/cookies";
import { getMainContainers, getMapStyleObject, getUrlReplacement, getWasmDetectionData } from "../utils";
import { createContextLostDialog } from "./dialogs";
import { hidePopup, setWasmIns } from "./util";

interface IWasmLoad {
	error: boolean;
}

export default class WasmController {
	#selector: string;

	#wasm: Wasm;

	#mapping: WasmStoreMapping;

	#afterLoad: boolean;

	#loadPromise: ReturnType<typeof deferPromise<IWasmLoad>>;

	#hiddenPoisLists: Array<WasmHiddenPois>;

	/** spravce aktualniho hurzoru nad mapou */
	#cursorManager: CursorManager;

	/** globalni kurzor, ktery si nastavujeme dle okolnosti nad mapou */
	#globalCursor: object;

	/** kurzor pro pripady, ze se kliknutim do mapy ma zaroutovat (tedy s CTRL pri aktivnim routovaci) */
	#routeCursor: object;

	/** kurzor, ktery nastavujeme a odnastavujeme dle signalu pri focusnutem inputu nekde, kde se vyplnuje i klikem do mapy */
	#inputCursor: object;

	#mainContainers: ReturnType<typeof getMainContainers>;

	#wasmError: boolean;

	constructor() {
		this.#selector = "wasm-canvas";
		this.#afterLoad = false;
		this.#loadPromise = deferPromise<IWasmLoad>();
		this.#hiddenPoisLists = [];
		this.#globalCursor = null;
		this.#routeCursor = null;
		this.#inputCursor = null;
		this.#wasmError = false;
		this.#mainContainers = getMainContainers();
	}

	start(loadWasm: boolean) {
		const usp = new URLSearchParams(location.search);

		// start 3d - mozne pak wasm
		if (CONFIG.wasmEnabled && usp.get("m3d") === "1") {
			const id = Scene.addListener("provider-change", (event: { data: { newProvider: string; oldProvider: string; } }) => {
				if (event.data.newProvider === "map") {
					Scene.removeListeners([id]);
					this.#build();
					this.#run();
				}
			});

			return Promise.resolve({
				error: false,
			});
		}

		// zapneme wasm z url
		if (!CONFIG.wasmEnabled && new URLSearchParams(location.search).get("force-wasm") === "1") {
			setItem(CONFIG.wasmCookie, "1");
			location.reload();

			return Promise.resolve({
				error: false,
			});
		}

		if (loadWasm) {
			this.#setWasmVisibility(true);
			this.#build();
			this.#run();
		} else {
			this.#loadPromise.resolve({
				error: false,
			});
		}

		return this.#loadPromise.promise;
	}

	get wasm() {
		return this.#wasm;
	}

	get stats() {
		if (this.#wasm) {
			const wasmStats = this.#wasm.stats;
			const mappingStats = this.#mapping.stats;

			return {
				wasmStats,
				mappingStats,
			};
		}

		return null;
	}

	createHiddenPoiList(name: string) {
		const hiddenList = new WasmHiddenPois(name);

		// pripojine wasm nebo pridame k pozdejsimu pridani
		if (this.#wasm) {
			hiddenList.wasm = this.#wasm;
		} else {
			this.#hiddenPoisLists.push(hiddenList);
		}

		return hiddenList;
	}

	#build() {
		const canvas = document.createElement("canvas");

		canvas.classList.add(this.#selector);
		canvas.style.width = `100%`;
		canvas.style.height = `100%`;

		this.#mainContainers.wasm.append(canvas);
		this.#cursorManager = new CursorManager(canvas);
		// nahodime si jako prvni kurzor, abychom ho mohli menit a on byl tim nejspodnejsim
		this.#globalCursor = this.#cursorManager.add("default");
		this.#startListeningToComponents();

		const currentState = store.getState().map;
		const detectData = getWasmDetectionData();
		const wasmDebug = Mapy.CUS.userweb?.wasmDebug;

		const wasm = new Wasm({
			parentElem: this.#mainContainers.wasm,
			// @ts-ignore
			lang: CONFIG.defaultLang,
			debug: wasmDebug,
			dpi: window.devicePixelRatio,
			isMobile: detectData.isMobile,
			urlReplacement: getUrlReplacement(),
			maxFps: detectData.maxFps,
			release: !wasmDebug,
			canvasQuery: `.${this.#selector}`,
			position: {
				lon: currentState.longitude,
				lat: currentState.latitude,
				zoom: currentState.zoom,
			},
			mapStyle: getMapStyleObject(currentState.mapset as TWasmBaseMaps, currentState.overlays, currentState.ophotoTimeline, currentState.desaturate),
			onMapClick: data => {
				onMapClick(data, store, CONFIG, this.#mapping, !!this.#inputCursor);
			},
			onHover: data => onHover(data, store, this.#cursorManager, this.#mapping, wasm),
			onDragStart: (wasmId, event) => onDragStart(wasmId, event, store, this.#cursorManager, this.#mapping, wasm),
			onDragMove: (id, event) => onDragMove(id, event, store, this.#cursorManager, wasm),
			onDragEnd: (id, event) => onDragEnd(id, event, store, this.#cursorManager, wasm),
			onContextMenu: (event, poi, wasmId) => {
				this.#contextMenu(event, poi, wasmId);
			},
			onBestfit: coordinates => {
				store.dispatch(setFit(coordinates));
			},
			onDblClick: async event => {
				const zoom = await this.#wasm.getZoom() + 1;

				this.#wasm.zoomToPixel({ x: event.clientX, y: event.clientY }, zoom);
			},
			onMapRedraw: async () => {
				if (!this.#afterLoad) {
					return;
				}

				const position = await this.#wasm.getCenterZoom();

				store.dispatch(setCoordinates({
					longitude: position.lon,
					latitude: position.lat,
					zoom: position.zoom,
					lastSource: "map",
				}));
				Mapy.getComponent("history").save();
			},
			onContextLost: () => {
				createContextLostDialog();
			},
			onResize: isTouch => {
				if (isTouch) {
					/** Skryjeme popupy */
					hidePopup(true);
				}
			},
		});

		// update velikosti
		addListener({}, "width-change", () => {
			wasm.syncPort();
		});

		Scene.addListener("provider-change", (event: { data: { newProvider: string; oldProvider: string; } }) => {
			if (event.data.newProvider === "m3d" && event.data.oldProvider === "map") {
				this.#setWasmVisibility(false);
			}
		});
		Scene.addListener("provider-changed", (event: { data: { firstChange: boolean; name: string; } }) => {
			this.#setWasmVisibility(event.data.name === "map");
		});

		this.#wasm = wasm;
	}

	/**
	 * Tady poslouchame na activate/deactivate tech, co pro nas hraji roli a taky na signaly tech, u kterych je treba brat v potaz focus/blur a podobne
	 */
	#startListeningToComponents() {
		addListener({}, "distancemeter-activated", () => {
			this.#cursorManager.update(this.#globalCursor, "url(/img/distancemeter/default.png) 3 2, crosshair");
		});
		addListener({}, "distancemeter-deactivated", () => {
			this.#cursorManager.update(this.#globalCursor, "default");
		});
		addListener({}, "usermarks-activated", () => {
			this.#cursorManager.update(this.#globalCursor, "url(/img/distancemeter/add.png) 3 3, crosshair");
		});
		addListener({}, "usermarks-deactivated", () => {
			this.#cursorManager.update(this.#globalCursor, "default");
		});
		addListener({}, "inputmapclick-focus", () => {
			if (this.#inputCursor) {
				this.#cursorManager.remove(this.#inputCursor);
			}
			this.#inputCursor = this.#cursorManager.add("crosshair");
		});
		addListener({}, "inputmapclick-blur", () => {
			if (this.#inputCursor) {
				this.#cursorManager.remove(this.#inputCursor);
				this.#inputCursor = null;
			}
		});
		document.addEventListener("keydown", event => {
			if (Mapy.getComponent("route").isActive() && (event.ctrlKey || event.metaKey || event.keyCode === 17 || event.keyCode === 91 || event.keyCode === 92 || event.keyCode === 93 || event.keyCode === 224)) {
				if (this.#routeCursor) {
					this.#cursorManager.remove(this.#routeCursor);
				}
				this.#routeCursor = this.#cursorManager.add("crosshair");
			}
		}, false);
		document.addEventListener("keyup", event => {
			if (event.ctrlKey || event.metaKey || event.keyCode === 17 || event.keyCode === 91 || event.keyCode === 92 || event.keyCode === 93 || event.keyCode === 224) {
				if (this.#routeCursor) {
					this.#cursorManager.remove(this.#routeCursor);
					this.#routeCursor = null;
				}
			}
		}, false);
	}

	async #run() {
		try {
			await this.#wasm.loadProm;

			// pripojime wasm
			for (const hiddenPoiList of this.#hiddenPoisLists) {
				hiddenPoiList.wasm = this.#wasm;
			}

			this.#hiddenPoisLists.length = 0;

			this.#mapping = new WasmStoreMapping(this.#wasm);
			this.#watchForMapChange();
			this.#afterLoad = true;

			// eslint-disable-next-line no-new
			new WasmSharePosition();
			setWasmIns(this.#wasm);

			this.#loadPromise.resolve({
				error: false,
			});
		} catch (exc) {
			this.#wasmError = true;
			this.#loadPromise.resolve({
				error: true,
			});
		}
	}

	#watchForMapChange() {
		const wt = watch(store.getState, "map", watchCoordinates);
		const wtMapset = watch(store.getState, "map", watchMapsetAndDesaturate);
		const wtOphotoTimeline = watch(store.getState, "map", watchOphotoTimeline);
		const wtFit = watch(store.getState, "map", watchFit);

		// zmeny souradnic
		store.subscribe(wt(async (newMap, oldMap) => {
			if (!newMap.ignore
				&& newMap.lastSource === "app"
				&& (
					newMap.longitude !== oldMap.longitude
					|| newMap.latitude !== oldMap.latitude
					|| newMap.zoom !== oldMap.zoom
				)
			) {
				if (newMap.animating) {
					this.#wasm.setCenterZoomAnim(newMap.longitude, newMap.latitude, newMap.zoom);
				} else {
					await this.#wasm.setCenterZoom(newMap.longitude, newMap.latitude, newMap.zoom);
				}

				store.dispatch(setCoordinates({
					animating: false,
					lastSource: "map",
				}));
			}
		}));

		// zmeny mapsetu a overlayu
		store.subscribe(wtMapset(newMap => {
			this.#setMapset(newMap.mapset as TWasmBaseMaps, newMap.overlays, newMap.ophotoTimeline, newMap.desaturate);
		}));

		// zmena stari ortofoto snimku
		store.subscribe(wtOphotoTimeline(newMap => {
			this.#setMapset(newMap.mapset as TWasmBaseMaps, newMap.overlays, newMap.ophotoTimeline, newMap.desaturate);
		}));

		// zmena pozadovaneho bestfitu
		store.subscribe(wtFit(newMap => {
			// ochrana proti tomu, kdyby se tam nejak objevilo prazdne pole nebo tak neco
			if (newMap.fit.length === 2) {
				const mapViewSetup = this.#wasm.mapContent.getMapViewSetupToShowPoints([...newMap.fit[0], ...newMap.fit[1]], this.#wasm.module.CoordSystem.Wgs, this.#wasm.module.ShowElementsMode.MoveZoom, 24, 124, 24, 64);

				this.#wasm.mapControl.setMapView(mapViewSetup.mapViewSetup);
			}

			// nakonec musime vynulovat fit ve store, protoze jinak by nesel znovu nastavit ten samy (tedy nastavim, pak jakkoliv pohnu mapou, nastavim znovu a nic se nestane, protoze fit tam porad je)
			store.dispatch(setFit([]));
		}));
	}

	#setMapset(mapset: TWasmBaseMaps, overlays: Array<MAP_OVERLAYS> = [], ophotoTimeline = "", desaturate: boolean) {
		if (mapset in LAYER_MAPPING) {
			this.#wasm.setMapstyle(getMapStyleObject(mapset, overlays, ophotoTimeline, desaturate));
		} else {
			// eslint-disable-next-line no-console
			console.error(`Unknown mapset: ${mapset} (overlays: ${overlays})`);
		}
	}

	async #contextMenu(event: PointerEvent, pois: Array<IPoiData>, wasmId: number) {
		// zastavime udalost
		event.preventDefault();
		event.stopPropagation();

		// ziskame souradnice v mape
		const { clientX, clientY } = event;
		const wgs = await this.#wasm.coordsFromEvent(event);
		// vyrobime falesny element, na ktery se navaze menu
		const referenceElement = {
			getBoundingClientRect() {
				return {
					width: 0,
					height: 0,
					x: clientX,
					y: clientY,
					top: clientY,
					left: clientX,
					right: clientX,
					bottom: clientY,
				};
			},
		};

		// vyrobime menu
		const contextMenu = new MapContextMenu({
			anchor: referenceElement as HTMLElement,
			position: "bottom-start",
			clickOutIgnoredElements: [],
			onClose: () => {
				contextMenu.destroy();
			},
			onOpen: () => {
				Mapy.getComponent("logger").log("menu", {
					eventx: wgs.x.toFixed(6),
					eventy: wgs.y.toFixed(6),
				});
			},
			longitude: wgs.x,
			latitude: wgs.y,
			pois,
			wasmStoreId: this.#mapping.getWasmToStoreData(wasmId).storeId,
		});

		// skryjeme popupy
		hidePopup(true);

		// ukazeme menu
		contextMenu.open();
	}

	#setWasmVisibility(state: boolean) {
		if (this.#wasmError) {
			return;
		}

		this.#mainContainers.m3d.style.display = state ? "none" : "";
		this.#mainContainers.map.style.display = "none";
		this.#mainContainers.wasm.style.display = state ? "" : "none";
	}
}
